import React, { useCallback, useState } from 'react';
import { Badge, Button, Modal, Tippy } from '@superops/ui-design-system';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import {
  getTranslatedText,
  EventEmitter,
  toast,
  useActivityTracker
} from '@nile/components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import {
  CREATE_USER_ACTION_CONFIG,
  BULK_CREATE_USER_ACTION_CONFIG
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { RMMClient } from '@nile/graphql';
import { EXECUTED_SCRIPTS_ACTIONS } from '@nile/banyan/src/Graphql/Query/executed-scripts';

const StatusCell = (props) => {
  const { cell, intl } = props;
  const { executionStatus, script, asset, actionId } = cell?.row?.original;
  const { row } = cell;
  const modalData = row.original.statusMessage;
  const sanitizer = dompurifier;
  const [modalActive, setModalActive] = useState(false);
  const [submitActionLoading, setSubmitActionLoading] = useState(false);
  const toggleModal = useCallback(() => setModalActive(!modalActive), [
    modalActive
  ]);

  const getStatusColorMap = (status) => {
    switch (status) {
      case 'In Progress':
        return 'info';
      case 'Success':
        return 'success';
      case 'Failure':
        return 'critical';
      default:
        return 'critical';
    }
  };

  return (
    <div className="fx actr fx-sb">
      <Badge status={getStatusColorMap(executionStatus)}>
        {executionStatus === 'Offline Skipped' ? (
          <FormattedMessage id="asset.details.summary.activities.scriptSkipped" />
        ) : (
          executionStatus
        )}
      </Badge>
      <div className="fx actr g8 mr-10">
        {executionStatus === 'Failure' && (
          <Tippy
            interactive
            title={getTranslatedText(intl, 'common.script.run.again')}
          >
            <Button
              secondary
              size="tiny"
              onClick={() =>
                executeScript(
                  script,
                  asset?.assetId,
                  setSubmitActionLoading,
                  actionId
                )
              }
              monochrome
              loading={submitActionLoading}
              icon={
                <div className="flex actr">
                  <SVGIcon name="ReRun" size="sm" />
                </div>
              }
            />
          </Tippy>
        )}
        <Tippy
          interactive
          title={getTranslatedText(
            intl,
            'asset.executedScript.scriptOutput.btn'
          )}
        >
          <Button
            secondary
            size="tiny"
            onClick={toggleModal}
            monochrome
            icon={
              <div className="flex actr">
                <SVGIcon name="SoftwareBundle" size="sm" />
              </div>
            }
          />
        </Tippy>
      </div>
      <Modal
        open={modalActive}
        onClose={toggleModal}
        title={getTranslatedText(intl, 'asset.executedScript.output.header')}
        secondaryActions={[
          {
            content: getTranslatedText(intl, 'common.cancel'),
            onAction: toggleModal
          }
        ]}
      >
        <div>
          <div className="break-spaces p20">
            {modalData ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizer(modalData)
                }}
              />
            ) : (
              <FormattedMessage id="asset.script.no.output.to.display" />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

const executeScript = async (input, assetId, actions, actionId) => {
  actions(true);

  const scriptArguments = await fetchExecutedScriptsActions(actionId);

  const constructRequestInput = (script) => {
    const reqOptions = {};
    reqOptions.script = script ? { scriptId: script.scriptId } : null;
    reqOptions.scriptArguments =
      scriptArguments?.map((arg) => ({
        name: arg.name,
        value: arg.value
      })) || [];
    reqOptions.executionType = 'IMMEDIATE';
    return { input: reqOptions };
  };

  const variables = {
    ...constructRequestInput(input)
  };
  if (assetId) {
    variables.assetId = assetId;
  }

  RMMClient.mutate({
    mutation: CREATE_USER_ACTION_CONFIG,
    variables: variables
  })
    .then((res) => {
      if (res) {
        // EventEmitter.dispatch('refetchActivitiesOrScripts');
        toast('success', 'asset.details.runScript.scriptScheduled.success');
        useActivityTracker({
          event: `Executed Script View - ReRun failed script`,
          data: {
            type: `Run script from Executed Script View`,
            executionType: input?.executionType
          }
        });
      }
      actions(false);
    })
    .catch((err) => {
      actions(false);
    });
};

const fetchExecutedScriptsActions = async (actionId) => {
  try {
    const { data } = await RMMClient.query({
      query: EXECUTED_SCRIPTS_ACTIONS,
      fetchPolicy: 'network-only',
      variables: {
        actionId: actionId
      }
    });

    return data?.getActionDetails?.scriptArguments;
  } catch (error) {
    throw error;
  }
};

export default injectIntl(StatusCell);
